import { type FC } from 'react';
import { Link } from 'react-router-dom';
import {
  ListChecks, GitCompareArrows, Link2, Gauge, ShieldCheck, FileText, ArrowRight,
} from 'lucide-react';
import { MarketingShell, MarketingHeader } from './MarketingShell';

/**
 * /product — what Agentic Loan Audit does. Public, unauthenticated.
 */

const FEATURES: Array<{ icon: typeof ListChecks; title: string; body: string }> = [
  {
    icon: ListChecks,
    title: '12-point compliance checklist',
    body: 'Every loan file is checked against a fixed 12-point checklist — document completeness, CAM-vs-LOS cross-checks, and full eligibility recompute.',
  },
  {
    icon: GitCompareArrows,
    title: 'Dual-path verification',
    body: 'Each rule runs two ways — a deterministic engine and an AI reviewer — and the verdicts are reconciled, so agreement is high-confidence and disagreement is surfaced.',
  },
  {
    icon: Link2,
    title: 'Evidence-linked findings',
    body: 'Every finding cites its source — the exact CAM value, LOS field, or uploaded document — so reviewers can verify in one click instead of hunting.',
  },
  {
    icon: Gauge,
    title: 'Audit score & severity',
    body: 'A single score and severity breakdown (critical / high / medium / low) shows at a glance whether a file is clean or needs attention.',
  },
  {
    icon: ShieldCheck,
    title: 'Deviation & approver matrix',
    body: 'Negative checks are matched against documented deviations, and the claimed approval level is validated against the authorised approver matrix.',
  },
  {
    icon: FileText,
    title: 'One-click audit report',
    body: 'Generate a clean, shareable audit report that mirrors the on-screen checklist — issue detail, evidence, and the final score.',
  },
];

export const ProductPage: FC = () => (
  <MarketingShell>
    <MarketingHeader
      eyebrow="Product"
      title="The 12-point CAM-vs-LOS audit, automated."
      lead="Agentic Loan Audit reconciles every Credit Approval Memo against the Loan Origination System and the underlying documents — completeness, cross-checks, and recompute — and surfaces exactly where a file deviates."
    />

    <div className="mt-12">
      <h2 className="font-display text-title font-bold text-on-surface mb-4">What it does</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FEATURES.map(({ icon: Icon, title, body }) => (
          <div key={title} className="bg-surface-container-lowest border border-outline-variant rounded-2xl p-6">
            <div className="w-10 h-10 rounded-xl bg-secondary-container/60 flex items-center justify-center text-primary">
              <Icon className="w-5 h-5" aria-hidden="true" />
            </div>
            <h3 className="mt-4 font-display text-heading font-bold text-on-surface">{title}</h3>
            <p className="mt-1.5 text-label text-on-surface-variant leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="mt-12 flex justify-center">
      <Link
        to="/login"
        className="h-11 px-6 inline-flex items-center gap-2 rounded-lg bg-primary text-on-primary text-label font-semibold hover:bg-primary-container transition-colors"
      >
        Get started
        <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </Link>
    </div>
  </MarketingShell>
);

export default ProductPage;
